import { configureStore } from '@reduxjs/toolkit';
import authReducer from '@e-learning/shared/src/store/slices/authSlice';
import coursesReducer from '@e-learning/shared/src/store/slices/coursesSlice';
import quizReducer from '@e-learning/shared/src/store/slices/quizSlice';
import ratingsReducer from '@e-learning/shared/src/store/slices/ratingsSlice';
import commentsReducer from '@e-learning/shared/src/store/slices/commentsSlice';
import blogsReducer from '@e-learning/shared/src/store/slices/blogsSlice';
import { fetchCourses, fetchCourse } from './thunks/coursesThunks';
import { generateQuizForCourse } from './thunks/quizThunks';
import { fetchBlogs, fetchBlog } from './thunks/blogsThunks';
import type { RootState } from '@e-learning/shared/src/store/types';

// Create enhanced courses reducer that handles both regular actions and async thunks
const enhancedCoursesReducer = (state: any = coursesReducer(undefined, { type: '@@INIT' }), action: any) => {
  // Handle async thunks
  if (fetchCourses.pending.match(action)) {
    return { ...state, loading: true, error: null };
  }
  if (fetchCourses.fulfilled.match(action)) {
    return { ...state, courses: action.payload, loading: false, error: null };
  }
  if (fetchCourses.rejected.match(action)) {
    return { ...state, loading: false, error: action.payload as string };
  }
  if (fetchCourse.pending.match(action)) {
    return { ...state, loading: true, error: null };
  }
  if (fetchCourse.fulfilled.match(action)) {
    return { ...state, selectedCourse: action.payload, loading: false, error: null };
  }
  if (fetchCourse.rejected.match(action)) {
    return { ...state, loading: false, error: action.payload as string };
  }
  // Handle regular actions from the original slice
  return coursesReducer(state, action);
};

// Create enhanced quiz reducer that handles async thunks
const enhancedQuizReducer = (state: any = quizReducer(undefined, { type: '@@INIT' }), action: any) => {
  // Handle async thunks
  if (generateQuizForCourse.pending.match(action)) {
    return { ...state, loading: true, error: null };
  }
  if (generateQuizForCourse.fulfilled.match(action)) {
    return { ...state, currentQuiz: action.payload, currentQuestionIndex: 0, answers: {}, loading: false, error: null };
  }
  if (generateQuizForCourse.rejected.match(action)) {
    return { ...state, loading: false, error: action.payload as string };
  }
  // Handle regular actions from the original slice
  return quizReducer(state, action);
};

// Create enhanced blogs reducer that handles async thunks
const enhancedBlogsReducer = (state: any = blogsReducer(undefined, { type: '@@INIT' }), action: any) => {
  // Handle async thunks
  if (fetchBlogs.pending.match(action)) {
    return { ...state, loading: true, error: null };
  }
  if (fetchBlogs.fulfilled.match(action)) {
    return { ...state, blogs: action.payload, loading: false, error: null };
  }
  if (fetchBlogs.rejected.match(action)) {
    return { ...state, loading: false, error: action.error.message };
  }
  if (fetchBlog.pending.match(action)) {
    return { ...state, loading: true, error: null };
  }
  if (fetchBlog.fulfilled.match(action)) {
    return { ...state, selectedBlog: action.payload, loading: false, error: null };
  }
  if (fetchBlog.rejected.match(action)) {
    return { ...state, loading: false, error: action.error.message };
  }
  // Handle regular actions from the original slice
  return blogsReducer(state, action);
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    courses: enhancedCoursesReducer,
    quiz: enhancedQuizReducer,
    ratings: ratingsReducer,
    comments: commentsReducer,
    blogs: enhancedBlogsReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: ['persist/PERSIST'],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export type AppDispatch = typeof store.dispatch;
export type AppState = RootState;
